/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  ActionMenu,
  ActionMenuProps,
  DIALOG_SIZE_SM,
  DIALOG_SIZE_SM_MD,
  DialogContentProps,
  DialogProps,
} from '@squiz/sds';
import clsx from 'clsx';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { EditRouteForm } from '../../forms/EditRouteForm/EditRouteForm';
import { STATIC_SITES_DATA_IDS } from '../../StaticSites.constants';
import { DeleteRouteDialogContent } from '../DeleteRouteDialogContent/DeleteRouteDialogContent';
import { RouteActionMenuProps } from './RouteActionMenu.props';

export const RouteActionMenu = ({ clearCache, dropdownUp, siteUrl, ...mapping }: RouteActionMenuProps): JSX.Element => {
  const { t } = useTranslation();

  // State
  const { updateActiveDialog } = useDialogStore();

  // Labels
  const labels = {
    delete: t(CONFIGURATION_STRINGS.delete),
    deleteRoute: t(CONFIGURATION_STRINGS.deleteRoute),
    edit: t(CONFIGURATION_STRINGS.edit),
    editRoute: t(CONFIGURATION_STRINGS.editRoute),
    mappingActions: t(CONFIGURATION_STRINGS.mappingActions),
  };

  const handleDelete = async (): Promise<void> => {
    const deleteDialogProps: DialogProps = {
      classes: 'manage-routing__dialog',
      dialogContent: {
        renderContent: (props: DialogContentProps): JSX.Element => {
          const contentProps = { ...props };
          contentProps.customProps = { clearCache, id: mapping.id };

          return <DeleteRouteDialogContent {...contentProps} />;
        },
      },
      dialogSize: DIALOG_SIZE_SM,
      heading: labels.deleteRoute,
      stateHandler: useDialogStore,
    };
    updateActiveDialog(deleteDialogProps);
  };

  const handleEdit = async (): Promise<void> => {
    const editDialogProps: DialogProps = {
      classes: 'manage-routing__dialog',
      dialogContent: {
        renderContent: (props: DialogContentProps): JSX.Element => {
          const formProps = { ...props };
          formProps.customProps = { clearCache, ...mapping, siteUrl };

          return <EditRouteForm {...formProps} />;
        },
      },
      dialogSize: DIALOG_SIZE_SM_MD,
      heading: labels.editRoute,
      stateHandler: useDialogStore,
    };
    updateActiveDialog(editDialogProps);
  };

  // istanbul ignore next
  const actionMenuProps: ActionMenuProps = {
    buttonLabel: labels.mappingActions,
    classes: clsx('route-list__item-action-menu', {
      'route-list__item-action-menu--up': dropdownUp,
    }),

    itemList: [
      {
        action: async (): Promise<void> => handleEdit(),
        dataID: STATIC_SITES_DATA_IDS.actionEditRoute,
        key: STATIC_SITES_DATA_IDS.actionEditRoute,
        label: labels.edit,
      },
      {
        action: (): Promise<void> => handleDelete(),
        dataID: STATIC_SITES_DATA_IDS.actionDeleteRoute,
        key: STATIC_SITES_DATA_IDS.actionDeleteRoute,
        label: labels.delete,
      },
    ],
    sectionKey: 'mapping-actions',
  };

  return <ActionMenu {...actionMenuProps} />;
};
